/**
 * Wrapper props for Tab Region.
 *
 * Shared by edit.jsx and save.jsx so the editor preview and the saved markup
 * cannot drift — a static-save block that computes its wrapper twice is the
 * classic source of "block contains unexpected content" validation errors.
 */

import { getBlockGapValue } from '../../utils/helpers';

/**
 * Core only ever writes blockGap through the layout support, and a region
 * declares none — it is a placed content area, and core's layout CSS would
 * fight the grid the parent Tabs block puts it in. So the value is read here.
 * `__experimentalSkipSerialization` in block.json states that this file owns
 * it.
 *
 * The gap is opt-in by class. A region is otherwise a plain block container
 * whose children space themselves with their own margins, so turning every
 * one into a gapped column would re-space tabs nobody has touched. The class
 * and the property appear together, only once an author sets a value — which
 * also means an existing region saves exactly the markup it always did.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} Props for useBlockProps / useBlockProps.save.
 */
export function getWrapperProps( attributes ) {
	const { area } = attributes;
	const gap = getBlockGapValue( attributes.style?.spacing?.blockGap );

	return {
		className: gap
			? 'brandy-tabs__region has-block-gap'
			: 'brandy-tabs__region',
		'data-area': area || 'main',
		...( gap ? { style: { '--brandy-tabs-region-gap': gap } } : {} ),
	};
}
